import { ArticleFormValues, ArticleEditorState, BlogSingleResponse } from 'Models'
import { backendToTiptap, DEFAULT_EDITOR_CAPABILITIES } from './TipTapAdapter'

/**
 * Backend article detail -> editor form values + server-owned editor state.
 *
 * - excerpt reads ONLY `excerptOverride ?? ''`. Falling back to the computed
 *   `excerpt` would turn an auto-generated excerpt into a permanent override
 *   on the next save.
 * - SEO fields read the top-level editable overrides, never the computed
 *   nested `seo` object.
 * - slug / version / status are returned separately as ArticleEditorState.
 */

export interface BlogResponseToFormResult {
  values: ArticleFormValues
  editorState: ArticleEditorState
  /** Block types the editor can't display; saving would delete them. Warn the admin. */
  droppedBlockTypes: string[]
}

/**
 * @param post          Article detail from GET /tradegridplusblog/:slug
 * @param capabilities  Node/mark names the live editor supports (defaults to the current ArticleEditor setup)
 */
export const blogResponseToForm = (
  post: BlogSingleResponse,
  capabilities: ReadonlySet<string> = DEFAULT_EDITOR_CAPABILITIES,
): BlogResponseToFormResult => {
  const { document, droppedBlockTypes } = backendToTiptap(post.content, capabilities)

  const values: ArticleFormValues = {
    title: post.title,
    excerpt: post.excerptOverride ?? '',
    category: post.category ?? '',
    tags: post.tags ?? [],

    coverImage: post.coverImage ?? null,
    coverImageAltText: post.coverImageAltText ?? '',

    content: document,

    metaTitle: post.metaTitle ?? undefined,
    metaDescription: post.metaDescription ?? undefined,
    canonicalUrl: post.canonicalUrl ?? undefined,
    socialImageUrl: post.socialImageUrl ?? undefined,
  }

  const editorState: ArticleEditorState = {
    slug: post.slug,
    version: post.version,
    publishedStatus: post.publishedStatus,
    createdAt: post.createdAt,
    updatedAt: post.updatedAt,
  }

  return { values, editorState, droppedBlockTypes }
}
